import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Messages } from 'primereact/messages';
import { getApiClient } from '../../../api';
import type { LanguageTranslationsResponse, LanguageTranslationsEditRequest } from '../../../api';
import { getErrorMessage } from '../../../utils/errors';
import { showErrorMessage, showSuccessMessage } from '../../utils/messages';
import { ProgressSpinner } from '../../../components/PrimereactComponents';
import { LanguageTranslationsForm } from './components/TranslationEnumForm';

export default function TranslationEnumEdit(): React.ReactElement | null {
    const { languageCode } = useParams<{ languageCode: string }>();
    const msgs = useRef<Messages>(null!);

    const [translations, setTranslations] = useState<LanguageTranslationsResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [processing, setProcessing] = useState<boolean>(false);

    useEffect(() => {
        if (!languageCode) {
            setLoading(false);
            return;
        }
        const fetchTranslations = async () => {
            try {
                const data = await getApiClient().getAdminTranslationsByLanguageCode(languageCode);
                setTranslations(data);
            } catch (error) {
                showErrorMessage(msgs, getErrorMessage(error) ?? 'Nepodařilo se načíst překlady');
            } finally {
                setLoading(false);
            }
        };
        fetchTranslations();
    }, [languageCode]);

    const onSubmit = async (data: LanguageTranslationsEditRequest): Promise<void> => {
        if (!languageCode) return;

        setProcessing(true);
        try {
            await getApiClient().putAdminTranslationsByLanguageCode(languageCode, data);
            showSuccessMessage(msgs, 'Překlady byly úspěšně uloženy');

            window.scrollTo({top: 0, behavior: 'smooth'});
        } catch (error) {
            showErrorMessage(msgs, getErrorMessage(error) ?? 'Nepodařilo se uložit překlady', { replace: true });
        } finally {
            setProcessing(false);
        }
    };

    if (loading) return <ProgressSpinner />;

    return (
        <>
            <Messages ref={msgs} className='page__messages' />

            <LanguageTranslationsForm 
                translations={translations}
                onSubmit={onSubmit}
                processing={processing}
            />
        </>
    );
}
